
import { useRef, useState, useEffect, useLayoutEffect } from 'preact/hooks'


// linear interpolation

export const lerp = (v0, v1, t) => v0*(1-t)+v1*t


// easing functions (input into lerp as 𝑇)

export const elastic = n => n * (33 * n * n * n * n - 106 * n * n * n + 126 * n * n - 67 * n + 15)
export const easeInQuad = t => t*t
export const easeOutQuad = t => t*(2-t)
export const easeInOutQuad = t => t<.5 ? 2*t*t : -1+(4-2*t)*t
export const easeInCubic = t => t*t*t
export const easeOutCubic = t => (--t)*t*t+1
export const easeInOutCubic = t => t<.5 ? 4*t*t*t : (t-1)*(2*t-2)*(2*t-2)+1
export const easeInQuart = t => t*t*t*t
export const easeOutQuart = t => 1-(--t)*t*t*t
export const easeInOutQuart = t => t<.5 ? 8*t*t*t*t : 1-8*(--t)*t*t*t
export const easeInOutQuint = (t) => t < 0.5 ? 16 *t*t*t*t*t : 1-Math.pow(-2*t+2, 5)/2

export const exponential = n => Math.pow(2, 10 * (n - 1))


/*
	usage:
	const [ref, display, animateIn] = useTransition(toggle : bool)
	const styles = animateIn ? { opacity:1 } : { opacity:0 }
	return {display && <div ref={ref} styles={styles} />}
*/

export const useTransition = (toggle) => {

	const ref = useRef(null)
	const [display, setDisplay] = useState(false)
	const [animateIn, setAnimateIn] = useState(false)

	const onTransitionEnd = (event) => {
		// stop events bubbling up to other elements
		event && event.stopPropagation()

		// make sure we're only firing on the current elem’s event and not its children
		if (event && event.target !== event.currentTarget) return false

		// only fire this once
		event && event.currentTarget.removeEventListener(event.type, onTransitionEnd)

		// remove the display
		setDisplay(false)
	}

	useLayoutEffect(() => {

		// intent to show, and is not displayed
		if (toggle && !display) setDisplay(true)

		// intent to show, is displayed, but not yet animated in
		if (toggle && display && !animateIn) {
			// sync dom read, to trigger animation
			ref.current.getBoundingClientRect()
			setAnimateIn(true)
		}           

		// intent to hide, is displayed, but not yet animated out
		if (!toggle && display && animateIn) {
			setAnimateIn(false)
			ref.current && ref.current.addEventListener(transitionEndEvent, onTransitionEnd)
		}

	}, [toggle, display])

	return [ref, display, animateIn]
}

const transitionEndEvent = (() => {
	if (typeof window === 'undefined') return 'transitionend'
	let t
	const el = document.createElement('div')
	const transitions = {
		'transition' : 'transitionend',
		'OTransition' : 'oTransitionEnd',
		'MozTransition' : 'transitionend',
		'WebkitTransition' : 'webkitTransitionEnd'
	}
	for (t in transitions) {
		if (el.style[t] !== undefined) {
		   return transitions[t]
		}
	}
})()


export const useEasing = (value, duration = 500, easing = easeOutQuart) => {

	const animate = true
	const prevAnimation = useRef(null)

	const [to, setTo] = useState(value)
	const [current, setCurrent] = useState(value)

	const setImmediately = (x) => {
		setCurrent(x)
		setTo(x)
	}

	useEffect(
		() => {
			if (!animate) return false
			// cancel any previous animations
			window.cancelAnimationFrame(prevAnimation.current)
			// only run animation frames when new value is different
			prevAnimation.current = (current !== to) && animateFrames(
				duration, 
				(x) => setCurrent(lerp(current, to, easing(x)))
			)			
		}, 
		[to]
	)

	return [ current, setTo, setImmediately ]
}


export const animateFrames = (duration, callback = (x) => {}) => {

	let loop 

	loop = window.requestAnimationFrame((startTime) => {
		
		const endTime = startTime + duration
		const handler = (time) => {
			const completed = (time >= endTime) ? 1 : (time - startTime) / duration
			callback(completed)
			if (completed >= 1) return 
			loop = window.requestAnimationFrame(handler)
		}

		loop = window.requestAnimationFrame(handler)
	})

	return loop
}